import type { GtmBrainDoc } from "../api/GtmApi";

export type PersonaListItem = {
  key: string;
  index: number;
  doc: GtmBrainDoc;
  label: string;
  filled: number;
  total: number;
  emoji: string;
};

const PERSONA_EMOJIS = ["🧑‍💻", "🛠️", "🧪", "📋", "👔", "🧭", "🤝", "💡", "📊", "🔧"];

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

function hasList(value?: string[] | null): boolean {
  return !!value && value.some((v) => v.trim().length > 0);
}

export function personaFill(doc: GtmBrainDoc): { filled: number; total: number } {
  const checks = [
    hasText(doc.name),
    hasText(doc.seniority),
    hasText(doc.description),
    hasList(doc.topPains),
    hasList(doc.wateringHoles),
  ];
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

export function personaLabel(doc: GtmBrainDoc, index: number): string {
  const name = (doc.name || "").trim();
  if (name) return name;
  return `Persona ${index + 1}`;
}

/** Stable key — keep in sync with brainToFlow docLeaves ids. */
export function personaKey(doc: GtmBrainDoc, personaIndex: number): string {
  return doc.id || `doc-persona-${personaIndex}`;
}

export function listPersonas(docs: GtmBrainDoc[]): PersonaListItem[] {
  const personasOnly = docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => doc.docType === "persona");
  return personasOnly.map(({ doc, index }, personaIndex) => {
    const fill = personaFill(doc);
    return {
      key: personaKey(doc, personaIndex),
      index,
      doc,
      label: personaLabel(doc, personaIndex),
      filled: fill.filled,
      total: fill.total,
      emoji: PERSONA_EMOJIS[personaIndex % PERSONA_EMOJIS.length],
    };
  });
}

export type PersonaFieldId =
  | "name"
  | "seniority"
  | "description"
  | "topPains"
  | "wateringHoles";

export function personaSections(doc: GtmBrainDoc): Array<{ id: PersonaFieldId; label: string; body: string }> {
  return [
    { id: "name", label: "Name", body: doc.name || "" },
    { id: "seniority", label: "Seniority", body: doc.seniority || "" },
    { id: "description", label: "Description", body: doc.description || "" },
    {
      id: "topPains",
      label: "Top pains",
      body: (doc.topPains || []).filter(Boolean).join("\n"),
    },
    {
      id: "wateringHoles",
      label: "Watering holes",
      body: (doc.wateringHoles || []).filter(Boolean).join("\n"),
    },
  ];
}

export function parsePersonaList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter(Boolean);
}
